<template>
  <div class="container process-demand-letter-container mb-3">
    <div
      v-if="status === GENERAL_INFO_STATUS()"
      class="upload-form-container mb-3"
    >
      <start-processing
        :session-id="sessionId"
        :handle-fetch-session-info="handleFetchSessionInfo"
      />
    </div>

    <div
      v-if="
        status === COMPLETED_STATUS() || status === RETRIEVAL_PROCESSED_STATUS()
      "
      class="d-flex flex-column"
    >
      <editor-section
        v-if="status === COMPLETED_STATUS()"
        v-model="demandLetter"
        title="Demand Letter"
        :editor="editor"
        :editor-config="editorConfig"
      >
        <button
          class="btn btn-primary fw-bold align-self-center mt-3"
          @click="handleSaveDemandLetter"
        >
          Save
        </button>
      </editor-section>

      <details class="w-100 mb-3" open>
        <summary class="text-start">
          <h2 class="d-inline fs-4">Retrieved Data</h2>
        </summary>

        <div class="ps-2 mt-3">
          <editor-section
            v-model="facts"
            title="Facts"
            :editor="editor"
            :editor-config="editorConfig"
          />

          <editor-section
            v-model="liability"
            title="Liability"
            :editor="editor"
            :editor-config="editorConfig"
          />

          <editor-section
            v-model="injuriesMedicals"
            title="Injuries/Medicals"
            :editor="editor"
            :editor-config="editorConfig"
          />

          <editor-section
            v-model="pipBills"
            title="PIP/Bills"
            :editor="editor"
            :editor-config="editorConfig"
          />
        </div>

        <button
          class="btn btn-primary fw-bold align-self-center"
          @click="handleUpdateRetrieval"
        >
          Save & Generate
        </button>
      </details>

      <details v-if="files" class="w-100">
        <summary class="text-start">
          <h3 class="d-inline fs-4">Raw PDF Data</h3>
        </summary>

        <div v-for="file in files" :key="file.id">
          <h4>{{ file.name }}</h4>
          <textarea v-model="file.ocr" class="textarea w-100 my-3" />
        </div>

        <button
          class="btn btn-primary fw-bold align-self-center"
          @click="handleUpdateOcr"
        >
          Save & Retrieve
        </button>
      </details>
    </div>

    <div
      v-if="
        status === OCR_PROCESSING_STATUS() ||
        status === RETRIEVAL_PROCESSING_STATUS() ||
        status === GENERATOR_PROCESSING_STATUS()
      "
      class="processing-status-container d-flex align-items-center flex-md-column"
    >
      <div class="spinner-container">
        <div
          class="spinner-border"
          style="width: 3rem; height: 3rem"
          role="status"
        ></div>
        <div class="fw-bold fs-5 mb-3">
          <div class="mb-3">
            We are processing your documents. Please wait...
          </div>
          <div class="mb-3">Stage: {{ formatStatus(status) }}</div>
          <div>
            <span v-if="status === RETRIEVAL_PROCESSING_STATUS()">
              ETA: 5 minutes
            </span>
            <span v-else-if="status === GENERATOR_PROCESSING_STATUS()">
              ETA: 2 minutes
            </span>
          </div>
        </div>
      </div>
      <template v-if="status === OCR_PROCESSING_STATUS()">
        <progress-bar
          v-if="sessionProgress > 0"
          class="w-100"
          :progress="sessionProgress * 100"
          :progress-text="progressMessage"
        />
        <span v-if="remainingTime" class="fw-bold fs-5"
          >ETA: {{ formatTime(remainingTime) }}
        </span>
      </template>
    </div>

    <div
      v-if="
        status === OCR_PROCESSING_FAILED_STATUS() ||
        status === RETRIEVAL_PROCESSING_FAILED_STATUS() ||
        status === GENERATOR_PROCESSING_FAILED_STATUS()
      "
    >
      <div class="fw-bold fs-5">Error: {{ formatStatus(status) }}</div>
    </div>

    <div
      v-if="formatStatus(status) === UNKNOWN_STATUS()"
      class="fw-bold fs-5 mb-3"
    >
      Unknown Status: {{ status }}
    </div>
    <div v-if="error" class="text-start fw-bold mb-4 mt-2 text-danger">
      {{ error }}
    </div>
  </div>
</template>

<script lang="ts">
import { getMappedStatus } from '@/common/utils';
import {
  reprocessGenerator,
  reprocessOcr,
  reprocessRetrieval,
} from '@/common/api';
import {
  COMPLETED_STATUS,
  GENERAL_INFO_STATUS,
  GENERATOR_PROCESSING_FAILED_STATUS,
  GENERATOR_PROCESSING_STATUS,
  OCR_PROCESSING_FAILED_STATUS,
  OCR_PROCESSING_STATUS,
  RESEND_REQUEST_TIMEOUT,
  RESEND_TIMEOUT_MAP,
  RETRIEVAL_PROCESSED_STATUS,
  RETRIEVAL_PROCESSING_FAILED_STATUS,
  RETRIEVAL_PROCESSING_STATUS,
  StatusKey,
  UNKNOWN_STATUS,
} from '@/common/constants';
import { defineComponent } from 'vue';

import {
  Bold,
  ClassicEditor,
  Essentials,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Italic,
  Link,
  List,
  Markdown,
  Paragraph,
  Undo,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import ProgressBar from '@/components/ProgressBar.vue';
import errorHandlerMixin from '@/mixins/errorHandlerMixin';
import EditorSection from '@/components/EditorSection.vue';
import StartProcessing from '@/components/StartProcessing.vue';
import { FileRecord } from '@/types/FileRecord';

export default defineComponent({
  components: {
    StartProcessing,
    EditorSection,
    ProgressBar,
  },

  mixins: [errorHandlerMixin],

  props: {
    status: {
      type: String as () => StatusKey,
      required: true,
    },
    sessionData: {
      type: Object,
      required: true,
    },
    handleFetchSessionInfo: {
      type: Function,
      required: true,
    },
    salutation: {
      type: String,
      default: '',
    },
    sessionId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      // Session processing progress as a number (0–1).
      sessionProgress: 0,
      // Descriptive message for the current session progress.
      progressMessage: '',
      // Initial progress value for calculating remaining time.
      initialSessionProgress: null as number | null,
      // Progress start time for calculating remaining time.
      sessionProgressStartTime: null as number | null,
      // Estimated remaining time to complete progress in milliseconds.
      remainingTime: null as number | null,

      files: [] as FileRecord[],
      facts: '',
      liability: '',
      injuriesMedicals: '',
      pipBills: '',
      demandLetter: '',
      pollingInterval: null as ReturnType<typeof setTimeout> | null,
      error: '',

      editor: ClassicEditor,
      editorConfig: {
        plugins: [
          Markdown,
          Bold,
          Essentials,
          Italic,
          Paragraph,
          Undo,
          Heading,
          FontFamily,
          FontSize,
          FontColor,
          Link,
          List,
        ],
        toolbar: {
          items: [
            'undo',
            'redo',
            '|',
            'heading',
            '|',
            'fontfamily',
            'fontsize',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'bold',
            'italic',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'link',
          ],
          shouldNotGroupWhenFull: false,
        },
      },
    };
  },

  watch: {
    status(newStatus, oldStatus) {
      if (newStatus !== oldStatus) {
        this.stopPolling();
        this.startPollingForStatus();
      }
    },
  },

  created() {
    this.startPollingForStatus();
    this.setFields();
  },

  beforeUnmount() {
    this.stopPolling();
  },

  methods: {
    RETRIEVAL_PROCESSING_FAILED_STATUS() {
      return RETRIEVAL_PROCESSING_FAILED_STATUS;
    },

    OCR_PROCESSING_FAILED_STATUS() {
      return OCR_PROCESSING_FAILED_STATUS;
    },

    GENERAL_INFO_STATUS() {
      return GENERAL_INFO_STATUS;
    },

    COMPLETED_STATUS() {
      return COMPLETED_STATUS;
    },

    GENERATOR_PROCESSING_STATUS() {
      return GENERATOR_PROCESSING_STATUS;
    },

    RETRIEVAL_PROCESSING_STATUS() {
      return RETRIEVAL_PROCESSING_STATUS;
    },

    RETRIEVAL_PROCESSED_STATUS() {
      return RETRIEVAL_PROCESSED_STATUS;
    },

    OCR_PROCESSING_STATUS() {
      return OCR_PROCESSING_STATUS;
    },

    GENERATOR_PROCESSING_FAILED_STATUS() {
      return GENERATOR_PROCESSING_FAILED_STATUS;
    },

    UNKNOWN_STATUS() {
      return UNKNOWN_STATUS;
    },

    startPolling(milliseconds: number) {
      this.pollingInterval = setInterval(() => {
        this.handleFetchSessionInfo(false)
          .then(() => {
            if (this.status === OCR_PROCESSING_STATUS) {
              this.sessionProgress = this.sessionData.progress;
              this.progressMessage = this.sessionData.progressMessage;

              this.initialSessionProgress =
                this.initialSessionProgress || this.sessionProgress;
              this.sessionProgressStartTime =
                this.sessionProgressStartTime || Date.now();

              this.remainingTime = this.calculateRemainingTime(
                this.sessionProgress,
                this.initialSessionProgress,
                this.sessionProgressStartTime,
              );
              return;
            }

            if (
              this.status !== COMPLETED_STATUS &&
              this.status !== RETRIEVAL_PROCESSED_STATUS
            ) {
              return;
            }

            this.setFields();
            this.stopPolling();
          })
          .catch((error: unknown) => {
            this.handleError(error, 'Failed to poll session info: ');
            this.stopPolling();
          });
      }, milliseconds);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    getResendTimeout(status: StatusKey): number | null {
      return RESEND_TIMEOUT_MAP[status] || null;
    },

    startPollingForStatus() {
      if (
        this.status === this.OCR_PROCESSING_STATUS() ||
        this.status === this.RETRIEVAL_PROCESSING_STATUS() ||
        this.status === this.GENERATOR_PROCESSING_STATUS()
      ) {
        const timeout = this.getResendTimeout(this.status);
        if (timeout !== null) {
          this.startPolling(timeout);
        }
      }
    },

    calculateRemainingTime(
      progress: number,
      startProgress: number,
      startTime: number,
    ): number {
      const elapsedTime = (Date.now() - startTime) / 1000;
      const deltaProgress = progress - startProgress;

      if (deltaProgress <= 0 || elapsedTime <= 0) {
        return 0;
      }

      const speed = deltaProgress / elapsedTime;
      const remainingProgress = 1.0 - progress;
      return remainingProgress / speed;
    },

    formatTime(seconds: number): string {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return [this.format(m, 'minute'), this.format(s, 'second')]
        .filter(Boolean)
        .join(' ');
    },

    format(n: number, text: string): string {
      return n > 0 ? `${n} ${text}${n !== 1 ? 's' : ''}` : '';
    },

    setFields() {
      this.files = this.sessionData.files;
      this.facts = this.sessionData.facts;
      this.liability = this.sessionData.liability;
      this.injuriesMedicals = this.sessionData.injuriesMedicals;
      this.pipBills = this.sessionData.pipBills;
      this.demandLetter = this.sessionData.demandLetter;
    },

    async handleUpdateOcr() {
      try {
        const requestPayload = {
          files: this.files.map((file) => ({
            fileID: file.id,
            fileOCR: file.ocr,
          })),
        };

        const response = await reprocessOcr(requestPayload, this.sessionId);

        if (response.status === 200) {
          await this.handleFetchSessionInfo();
          this.startPolling(RESEND_REQUEST_TIMEOUT);
        }
      } catch (error: unknown) {
        this.handleError(error, 'Failed to reprocess OCR: ');
      }
    },

    async handleUpdateRetrieval() {
      const retrievalMessage = {
        id: this.sessionId,
        facts: this.facts,
        liability: this.liability,
        injuriesMedicals: this.injuriesMedicals,
        pipBills: this.pipBills,
      };

      try {
        const response = await reprocessRetrieval(retrievalMessage);

        if (response.status === 200) {
          await this.handleFetchSessionInfo();
          this.startPolling(RESEND_REQUEST_TIMEOUT);
        }
      } catch (error: unknown) {
        this.handleError(error, 'Failed to reprocess retrieval: ');
      }
    },

    async handleSaveDemandLetter() {
      try {
        const response = await reprocessGenerator({
          id: this.sessionId,
          demandLetter: this.demandLetter,
        });

        if (response.status === 200) {
          await this.handleFetchSessionInfo();
        }
      } catch (error: unknown) {
        this.handleError(error, 'Failed to reprocess generator: ');
      }
    },

    formatStatus(status: string) {
      return getMappedStatus(status);
    },
  },
});
</script>

<style scoped>
.textarea {
  height: 600px;
}
</style>

<style>
.ck-editor__editable {
  max-height: 600px;
  overflow-y: auto;
}
</style>
